import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Link } from '../../router';
import { colors } from '../../themes';
import Icon from '../../components/shared/Icons/Icon';
import messages from '../messages';
import {
  DEAL_TYPE_HOMES_FOR_SALE,
  DEAL_TYPE_HOMES_FOR_RENT,
  MAIN_DEAL_TYPES_ID,
} from '../../common/constants/homes';
import {
  notOnlyExistMainDealType,
  getPropertyTypesByDealTypeId,
  pickPropertyTypes,
  isMainDealType,
} from '../utils/homes';

const { noResults } = messages;

class SeoLinksOsm extends React.PureComponent {
  sortByLocaleLanguage = (textA = '', textB = '') => textA.localeCompare(textB);

  renderLinks = (links, isFetching) => {
    if (links.length === 0 && !isFetching) {
      return (
        <ul className="noResults" data-test="seo-links__location">
          <li>
            <FormattedMessage {...noResults} />
          </li>
        </ul>
      );
    }
    if (links.length === 0) {
      return (
        <ul className="seoLinksPlaceholders" data-test="seo-links__location">
          <li>
            <strong /> <span />
          </li>
          <li>
            <strong /> <span />
          </li>
        </ul>
      );
    }

    return (
      <ul className="seoLinksRegions" data-test="seo-links__location">
        {links
          .sort((linkA, linkB) => this.sortByLocaleLanguage(linkA.name, linkB.name))
          .map(link => (
            <li key={link.id}>
              <strong>{link.totalResults}</strong>
              <Link route={link.url}>
                <a title={link.name}>{link.name}</a>
              </Link>
            </li>
          ))}
      </ul>
    );
  };

  render() {
    const {
      propertyTypes,
      parentDealType,
      dealType,
      dealTypes,
      currentPropertyType,
      links,
      isFetching,
    } = this.props;

    if (!propertyTypes) {
      return null;
    }

    const dealTypesArray = Object.keys(dealTypes).map(Number);
    const showProperties = notOnlyExistMainDealType(dealTypesArray);
    const propertyTypesForDealType = {};
    const pickedPropertyTypesForDealType = {};

    MAIN_DEAL_TYPES_ID.forEach(value => {
      propertyTypesForDealType[value] = getPropertyTypesByDealTypeId(value);
      let pickedProperties = pickPropertyTypes(propertyTypesForDealType[value], dealTypesArray);

      if (pickedProperties.length === 1 && isMainDealType(pickedProperties[0].dealTypeId)) {
        pickedProperties = [];
      }

      pickedPropertyTypesForDealType[value] = pickedProperties;
    });

    const routeName =
      parentDealType === DEAL_TYPE_HOMES_FOR_SALE ? 'home-deal-type-1' : 'home-deal-type-2';

    return (
      <div className="seoLinks" id="linking-hierarchy">
        <ul className="tabs">
          <li
            className={parentDealType === DEAL_TYPE_HOMES_FOR_SALE ? 'active' : null}
            data-test="seo-links-tab__type-sale"
          >
            <Link scroll={false} route="home-deal-type-1">
              <a className="">
                <FormattedMessage id={dealTypes['1']} />
              </a>
            </Link>
          </li>
          <li
            className={parentDealType === DEAL_TYPE_HOMES_FOR_RENT ? 'active' : null}
            data-test="seo-links-tab__type-rent"
          >
            <Link scroll={false} route="home-deal-type-2">
              <a className="">
                <FormattedMessage id={dealTypes['2']} />
              </a>
            </Link>
          </li>
        </ul>
        {showProperties && (
          <React.Fragment>
            {MAIN_DEAL_TYPES_ID.map(value => (
              <ul
                key={value}
                className={`property-deal-type ${value !== parentDealType ? 'hide' : ''}`}
              >
                {pickedPropertyTypesForDealType[value].map(pickedProperty => (
                  <li key={pickedProperty.dealTypeId}>
                    <Link scroll={false} route={`home-deal-type-${pickedProperty.dealTypeId}`}>
                      <a className="">
                        <span className={dealType === pickedProperty.dealTypeId ? 'selected' : ''}>
                          <FormattedMessage id={pickedProperty.translationTag} />
                        </span>
                      </a>
                    </Link>
                  </li>
                ))}
              </ul>
            ))}
          </React.Fragment>
        )}
        <ul className="property" data-test="seo-links__property-type">
          {isMainDealType(dealType) &&
            Object.keys(propertyTypes)
              .sort((propertyA, propertyB) =>
                this.sortByLocaleLanguage(propertyTypes[propertyA], propertyTypes[propertyB])
              )
              .map(key => (
                <li key={key}>
                  <Link scroll={false} route={routeName} params={{ property_type: key }}>
                    <a>{propertyTypes[key]}</a>
                  </Link>
                  <span className="arrow">
                    <Icon type="arrow-down" />
                  </span>
                  {key === currentPropertyType && this.renderLinks(links, isFetching)}
                </li>
              ))}
        </ul>
        {!currentPropertyType && this.renderLinks(links, isFetching)}
        <style jsx>
          {`
            .hide {
              display: none;
            }
            .property {
              list-style: none;
              padding: 0 1em;
              margin: 0;
            }
            .property li {
              border-bottom: 1px solid ${colors.grayExtraLight};
              position: relative;
              padding: 15px;
            }
            .property li > a {
              display: block;
            }
            .property li .arrow {
              color: ${colors.grayLight};
              position: absolute;
              right: 15px;
              top: 15px;
            }
            .tabs {
              background: #fff;
              border-bottom: 1px solid ${colors.grayExtraLight};
              box-shadow: 0 -1px 2px 0 rgba(0, 0, 0, 0.1);
              list-style: none;
              padding: 0;
              margin: 0;
            }
            .tabs li {
              display: inline-block;
              font-weight: bold;
              padding: 17px 10px;
              text-align: center;
              width: 50%;
            }
            .tabs .active {
              box-shadow: 0 1px 0 0 ${colors.blue};
            }
            .tabs .active a {
              color: ${colors.blue};
            }
            .tabs a {
              color: ${colors.grayDark};
              text-decoration: none;
            }
            .seoLinks :global(a) {
              text-decoration: none;
            }
            .seoLinks :global(.seoLinksRegions) {
              list-style: none;
              padding: 15px;
              margin: 0;
            }
            .seoLinks .property :global(.seoLinksRegions) {
              border-top: 1px solid ${colors.blueLight};
              padding: 15px 0 0;
              margin: 15px 0 0;
            }
            .seoLinks :global(.seoLinksRegions li) {
              border-bottom: none;
              padding: 8px 0;
            }
            .seoLinks :global(.seoLinksRegions li strong) {
              display: inline-block;
              padding-right: 10px;
              text-align: right;
              width: 75px;
            }
            .seoLinks :global(.seoLinksPlaceholders) {
              list-style: none;
              padding: 15px;
              margin: 0;
            }
            .seoLinks .property :global(.seoLinksPlaceholders) {
              border-top: 1px solid ${colors.blueLight};
              padding: 15px 0 0;
              margin: 15px 0 0;
            }
            .seoLinks :global(.seoLinksPlaceholders li) {
              border-bottom: none;
              padding: 8px 0;
            }
            .seoLinks :global(.seoLinksPlaceholders li strong) {
              background: ${colors.grayExtraLight};
              opacity: 0.5;
              display: inline-block;
              border-radius: 4;
              height: 15px;
              margin: 0 5px;
              width: 70px;
            }
            .seoLinks :global(.seoLinksPlaceholders li span) {
              background: ${colors.grayExtraLight};
              display: inline-block;
              border-radius: 4;
              height: 15px;
              width: 175px;
            }
            .seoLinks .property :global(.noResults) {
              border-top: 1px solid ${colors.blueLight};
              padding: 15px 0 0;
              margin: 15px 0 0;
              list-style: none;
            }
            .seoLinks :global(.noResults li) {
              border-bottom: none;
              padding: 8px 0;
              color: ${colors.gray};
            }
            .seoLinks .property-deal-type {
              padding: 1em;
              margin: 0;
            }
            .seoLinks .property-deal-type li {
              display: inline-block;
              padding: 5px;
              margin-bottom: 10px;
            }
            .seoLinks .property-deal-type li span {
              font-size: 12px;
              line-height: 1.3;
              background: #fff;
              padding: 0.6em 1.2em;
              border-radius: 25px;
              margin: 0;
              white-space: nowrap;
              color: #757575;
              border: solid 1px #bdbdbd;
            }
            .seoLinks .property-deal-type li .selected {
              color: #1481f7;
              border: solid 1px #1481f7;
            }
            :global(.rtl) .property li .arrow {
              left: 15px;
              right: auto;
            }
            :global(.rtl) .seoLinks :global(.seoLinksRegions li strong) {
              padding-left: 10px;
              padding-right: 0;
              text-align: left;
            }
          `}
        </style>
      </div>
    );
  }
}

SeoLinksOsm.defaultProps = {
  propertyTypes: null,
  links: [],
  currentPropertyType: null,
};

SeoLinksOsm.propTypes = {
  propertyTypes: PropTypes.object,
  links: PropTypes.array,
  parentDealType: PropTypes.number.isRequired,
  dealType: PropTypes.number.isRequired,
  dealTypes: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  currentPropertyType: PropTypes.string,
};

export default SeoLinksOsm;



// WEBPACK FOOTER //
// ./app/Index/components/SeoLinks.homes.js